import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Chip,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Hub as HubIcon,
  Refresh as RefreshIcon,
  Handshake,
  Apps,
  EmojiEvents,
  NewReleases,
  AttachMoney,
  People,
  Star,
  Build as SetupIcon,
  HelpOutline,
  ThumbUp,
} from '@mui/icons-material';
import api from '../../services/api';
import hubService, {
  Parceiro,
  MiniApp,
  NivelBeneficio,
  Novidade,
  HubStats,
} from '../../services/hub.service';
import { useTranslation } from 'react-i18next';

const CATEGORIAS_PARCEIRO = [
  'Alimentacao',
  'Saude',
  'Restaurantes',
  'Telecoms',
  'Servicos',
  'Moda',
  'Outros',
];

const CATEGORIAS_APP = [
  'Financeiro',
  'Viagem',
  'Telecoms',
  'Entretenimento',
  'Alimentacao',
  'Imobiliario',
  'Outros',
];

const TIPOS_NOVIDADE = [
  { value: 'feature', label: 'Nova Funcionalidade' },
  { value: 'update', label: 'Atualização' },
  { value: 'promo', label: 'Promoção' },
  { value: 'news', label: 'Notícia' },
];

const TIPOS_RECURSO = [
  { value: 'faq', label: 'FAQ' },
  { value: 'guia', label: 'Guia' },
  { value: 'video', label: 'Vídeo' },
  { value: 'contacto', label: 'Contacto' },
  { value: 'quick_action', label: 'Atalho rápido' },
];

const STATUS_FEATURE = [
  { value: 'pendente', label: 'Em votação' },
  { value: 'planeada', label: 'Planeada' },
  { value: 'em_desenvolvimento', label: 'Em desenvolvimento' },
  { value: 'concluida', label: 'Concluída' },
  { value: 'rejeitada', label: 'Rejeitada' },
];

const Hub: React.FC = () => {
  const { t } = useTranslation();
  const [mainTab, setMainTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);

  // Pioneiros state
  const [pioneiros, setPioneiros] = useState<any[]>([]);
  const [pioneiroLoading, setPioneiroLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyResult, setNotifyResult] = useState<any>(null);
  const [testEmail, setTestEmail] = useState('');
  const [pioneiroDlg, setPioneiroDlg] = useState(false);
  const [pioneiroDlgMode, setPioneiroDlgMode] = useState<'add' | 'edit'>('add');
  const [pioneiroDlgData, setPioneiroDlgData] = useState<any>({});
  const [pioneiroDlgLoading, setPioneiroDlgLoading] = useState(false);
  const [deleteConfirmDlg, setDeleteConfirmDlg] = useState<any>(null);

  // Data
  const [stats, setStats] = useState<HubStats | null>(null);
  const [parceiros, setParceiros] = useState<Parceiro[]>([]);
  const [apps, setApps] = useState<MiniApp[]>([]);
  const [niveis, setNiveis] = useState<NivelBeneficio[]>([]);
  const [novidades, setNovidades] = useState<Novidade[]>([]);
  const [recursos, setRecursos] = useState<any[]>([]);
  const [features, setFeatures] = useState<any[]>([]);
  const [votersDialog, setVotersDialog] = useState<{ open: boolean; feature: any | null; voters: any[]; loading: boolean }>({ open: false, feature: null, voters: [], loading: false });

  // Dialog state
  const [editDialog, setEditDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'parceiro' | 'app' | 'nivel' | 'novidade' | 'recurso' | 'feature'>('parceiro');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [formData, setFormData] = useState<any>({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, parceirosRes, appsRes, niveisRes, novidadesRes, recursosRes, featuresRes] = await Promise.allSettled([
        hubService.getStats(),
        hubService.getParceiros(),
        hubService.getApps(),
        hubService.getNiveis(),
        hubService.getNovidades(),
        hubService.getRecursos(),
        hubService.getFeatureRequests(),
      ]);

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value);
        if (statsRes.value.needsSetup) {
          setNeedsSetup(true);
        }
      }
      if (parceirosRes.status === 'fulfilled') setParceiros(parceirosRes.value);
      if (appsRes.status === 'fulfilled') setApps(appsRes.value);
      if (niveisRes.status === 'fulfilled') setNiveis(niveisRes.value);
      if (novidadesRes.status === 'fulfilled') setNovidades(novidadesRes.value);
      if (recursosRes.status === 'fulfilled') setRecursos(recursosRes.value);
      if (featuresRes.status === 'fulfilled') setFeatures(featuresRes.value);
    } catch (err: any) {
      console.error('Error fetching hub data:', err);
      setError(err.message || t('hub.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const reloadPioneiros = useCallback(async () => {
    setPioneiroLoading(true);
    setNotifyResult(null);
    try {
      const r = await api.get('/admin/hub/pioneiros');
      setPioneiros(r.data?.data || []);
    } catch { /* ignore */ } finally { setPioneiroLoading(false); }
  }, []);

  useEffect(() => {
    if (mainTab === 8) reloadPioneiros();
  }, [mainTab, reloadPioneiros]);

  const handleSetup = async () => {
    setSetupLoading(true);
    setError(null);
    try {
      const result = await hubService.setupTables();
      setNeedsSetup(false);
      alert(`${t('hub.tablesCreated')}: ${result.created?.join(', ') || 'Hub'}`);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('hub.errorSetup'));
    } finally {
      setSetupLoading(false);
    }
  };

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  // Dialog handlers
  const handleOpenDialog = (type: typeof dialogType, item?: any) => {
    setDialogType(type);
    setSelectedItem(item || null);

    if (type === 'parceiro') {
      setFormData(item ? { ...item } : {
        nome: '',
        categoria: 'Outros',
        desconto: '',
        descricao: '',
        website: '',
        destaque: false,
        status: 'ACTIVE',
      });
    } else if (type === 'app') {
      setFormData(item ? { ...item } : {
        nome: '',
        descricao: '',
        icone: 'box',
        categoria: 'Outros',
        url: '',
        preco: 0,
        preco_original: null,
        gratuito: false,
        destaque: false,
        status: 'ACTIVE',
        ordem: 0,
      });
    } else if (type === 'nivel') {
      setFormData(item ? { ...item } : {
        nome: '',
        pontos_minimos: 0,
        pontos_maximos: 0,
        beneficios: '',
        cor: 'from-gray-400 to-gray-500',
        icone: 'star',
        status: 'ACTIVE',
      });
    } else if (type === 'novidade') {
      setFormData(item ? { ...item } : {
        titulo: '',
        descricao: '',
        conteudo: '',
        tipo: 'news',
        destaque: false,
        status: 'ACTIVE',
      });
    } else if (type === 'recurso') {
      setFormData(item ? { ...item } : {
        titulo: '',
        descricao: '',
        conteudo: '',
        tipo: 'faq',
        categoria: 'geral',
        icone: '',
        url: '',
        ordem: 0,
        status: 'ACTIVE',
      });
    } else if (type === 'feature') {
      setFormData(item ? { ...item } : {
        titulo: '',
        descricao: '',
        categoria: 'geral',
        status: 'pendente',
      });
    }

    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      setError(null);

      if (dialogType === 'parceiro') {
        if (selectedItem) {
          await hubService.updateParceiro(selectedItem.id, formData);
        } else {
          await hubService.createParceiro(formData);
        }
      } else if (dialogType === 'app') {
        if (selectedItem) {
          await hubService.updateApp(selectedItem.id, formData);
        } else {
          await hubService.createApp(formData);
        }
      } else if (dialogType === 'nivel') {
        if (selectedItem) {
          await hubService.updateNivel(selectedItem.id, formData);
        } else {
          await hubService.createNivel(formData);
        }
      } else if (dialogType === 'novidade') {
        if (selectedItem) {
          await hubService.updateNovidade(selectedItem.id, formData);
        } else {
          await hubService.createNovidade(formData);
        }
      } else if (dialogType === 'recurso') {
        if (selectedItem) {
          await hubService.updateRecurso(selectedItem.id, formData);
        } else {
          await hubService.createRecurso(formData);
        }
      } else if (dialogType === 'feature') {
        if (selectedItem) {
          await hubService.updateFeatureRequest(selectedItem.id, formData);
        } else {
          await hubService.createFeatureRequest(formData);
        }
      }

      setEditDialog(false);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('hub.errorSaving'));
    }
  };

  const handleDelete = async (type: typeof dialogType, id: number, name: string) => {
    if (!window.confirm(`Eliminar "${name}"?`)) return;

    try {
      setError(null);
      if (type === 'parceiro') await hubService.deleteParceiro(id);
      else if (type === 'app') await hubService.deleteApp(id);
      else if (type === 'nivel') await hubService.deleteNivel(id);
      else if (type === 'novidade') await hubService.deleteNovidade(id);
      else if (type === 'recurso') await hubService.deleteRecurso(id);
      else if (type === 'feature') await hubService.deleteFeatureRequest(id);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('hub.errorDeleting'));
    }
  };

  if (loading && !stats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {needsSetup && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={setupLoading ? <CircularProgress size={16} /> : <SetupIcon />}
              onClick={handleSetup}
              disabled={setupLoading}
            >
              {setupLoading ? t('common.loading') : t('shop.setupTables')}
            </Button>
          }
        >
          {t('hub.setupNeeded')}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('hub.title')}
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Main Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<HubIcon />} label={t('hub.dashboard')} iconPosition="start" />
          <Tab icon={<Handshake />} label={t('hub.partners')} iconPosition="start" />
          <Tab icon={<Apps />} label={t('hub.miniApps')} iconPosition="start" />
          <Tab icon={<EmojiEvents />} label={t('hub.levels')} iconPosition="start" />
          <Tab icon={<EmojiEvents />} label="Pontos" iconPosition="start" />
          <Tab icon={<NewReleases />} label={t('hub.news')} iconPosition="start" />
          <Tab icon={<HelpOutline />} label="Recursos" iconPosition="start" />
          <Tab icon={<ThumbUp />} label="Feature Requests" iconPosition="start" />
          <Tab icon={<People />} label="Pioneiros 🏅" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Dashboard Tab */}
      {mainTab === 0 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Handshake color="primary" />
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats?.total_parceiros || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('hub.partners')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Apps color="info" />
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats?.total_apps || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('hub.miniApps')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney color="success" />
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{formatCurrency(stats?.receita_apps)}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('hub.appsRevenue')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Star color="warning" />
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats?.total_pontos_distribuidos?.toLocaleString() || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('hub.pointsDistributed')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <People color="secondary" />
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats?.total_referrals || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">Referrals</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NewReleases color="error" />
                  <Box>
                    <Typography variant="h5" fontWeight={700}>{stats?.total_novidades || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('hub.news')}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Parceiros Tab */}
      {mainTab === 1 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Parceiros ({parceiros.length})</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('parceiro')}>
              {t('hub.newPartner')}
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell>Desconto</TableCell>
                  <TableCell align="center">Destaque</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={120} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parceiros.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('hub.noPartners')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  parceiros.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{p.nome}</Typography>
                        {p.website && (
                          <Typography variant="caption" color="text.secondary">{p.website}</Typography>
                        )}
                      </TableCell>
                      <TableCell>{p.categoria}</TableCell>
                      <TableCell>
                        <Chip size="small" color="success" label={p.desconto} />
                      </TableCell>
                      <TableCell align="center">
                        {p.destaque && <Star color="warning" fontSize="small" />}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={p.status === 'ACTIVE' ? 'success' : 'default'} label={p.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog('parceiro', p)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete('parceiro', p.id, p.nome)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Mini-Apps Tab */}
      {mainTab === 2 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Mini-Apps ({apps.length})</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('app')}>
              {t('hub.newApp')}
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>App</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="right">Preço</TableCell>
                  <TableCell align="center">Destaque</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={120} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('hub.noApps')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  apps.map((app) => (
                    <TableRow key={app.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{app.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">{app.descricao}</Typography>
                      </TableCell>
                      <TableCell>{app.categoria}</TableCell>
                      <TableCell align="right">
                        {app.gratuito ? (
                          <Chip size="small" color="info" label={t('hub.free')} />
                        ) : (
                          <Box>
                            <Typography fontWeight={600}>{formatCurrency(app.preco)}</Typography>
                            {app.preco_original && (
                              <Typography variant="caption" sx={{ textDecoration: 'line-through' }} color="text.secondary">
                                {formatCurrency(app.preco_original)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {app.destaque && <Star color="warning" fontSize="small" />}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={app.status === 'ACTIVE' ? 'success' : 'default'} label={app.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog('app', app)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete('app', app.id, app.nome)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Níveis Tab */}
      {mainTab === 3 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('hub.benefitLevels')} ({niveis.length})</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('nivel')}>
              {t('hub.newLevel')}
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nível</TableCell>
                  <TableCell align="right">Pontos Mínimos</TableCell>
                  <TableCell align="right">Pontos Máximos</TableCell>
                  <TableCell>Benefícios</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={120} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {niveis.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('hub.noLevels')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  niveis.map((nivel) => (
                    <TableRow key={nivel.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{nivel.nome}</Typography>
                      </TableCell>
                      <TableCell align="right">{(nivel.pontos_minimos ?? 0).toLocaleString()}</TableCell>
                      <TableCell align="right">{nivel.pontos_maximos != null ? nivel.pontos_maximos.toLocaleString() : '∞'}</TableCell>
                      <TableCell>
                        <Typography variant="caption">{nivel.beneficios?.substring(0, 50)}...</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={nivel.status === 'ACTIVE' ? 'success' : 'default'} label={nivel.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog('nivel', nivel)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete('nivel', nivel.id, nivel.nome)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pontos Configuration Tab */}
      {mainTab === 4 && <PontosConfigSection />}

      {/* Novidades Tab */}
      {mainTab === 5 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Novidades ({novidades.length})</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('novidade')}>
              {t('hub.newNews')}
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="center">Destaque</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={120} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {novidades.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">{t('hub.noNews')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  novidades.map((n) => (
                    <TableRow key={n.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{n.titulo}</Typography>
                        <Typography variant="caption" color="text.secondary">{n.descricao?.substring(0, 50)}...</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={n.tipo === 'feature' ? 'primary' : n.tipo === 'promo' ? 'success' : 'default'}
                          label={n.tipo === 'feature' ? t('hub.typeFeature') : n.tipo === 'update' ? t('hub.typeUpdate') : n.tipo === 'promo' ? t('hub.typePromo') : n.tipo === 'news' ? t('hub.typeNews') : n.tipo}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {n.destaque && <Star color="warning" fontSize="small" />}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={n.status === 'ACTIVE' ? 'success' : 'default'} label={n.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog('novidade', n)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete('novidade', n.id, n.titulo)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Recursos Tab */}
      {mainTab === 6 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Recursos ({recursos.length})</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('recurso')}>
              Novo recurso
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="center">Ordem</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={120} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recursos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Sem recursos</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  recursos.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{r.titulo}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.descricao ? (r.descricao.length > 60 ? r.descricao.substring(0, 60) + '…' : r.descricao) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={TIPOS_RECURSO.find(tp => tp.value === r.tipo)?.label || r.tipo}
                          color={r.tipo === 'faq' ? 'primary' : r.tipo === 'quick_action' ? 'secondary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{r.categoria}</TableCell>
                      <TableCell align="center">{r.ordem ?? 0}</TableCell>
                      <TableCell>
                        <Chip size="small" color={r.status === 'ACTIVE' ? 'success' : 'default'} label={r.status} />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog('recurso', r)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete('recurso', r.id, r.titulo)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Feature Requests Tab */}
      {mainTab === 7 && (
        <Paper>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Feature Requests ({features.length})</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('feature')}>
              Nova proposta
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align="center">Votos</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell width={120} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {features.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Sem feature requests</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  features.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{f.titulo}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {f.descricao ? (f.descricao.length > 60 ? f.descricao.substring(0, 60) + '…' : f.descricao) : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>{f.categoria}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Clique para ver quem votou">
                          <Chip
                            size="small"
                            icon={<ThumbUp sx={{ fontSize: 14 }} />}
                            label={f.votos_reais ?? 0}
                            color={(f.votos_reais ?? 0) > 0 ? 'primary' : 'default'}
                            onClick={async () => {
                              setVotersDialog({ open: true, feature: f, voters: [], loading: true });
                              try {
                                const res = await api.get(`/admin/hub/feature-requests/${f.id}/voters`);
                                setVotersDialog(d => ({ ...d, voters: res.data?.data || [], loading: false }));
                              } catch {
                                setVotersDialog(d => ({ ...d, loading: false }));
                              }
                            }}
                            clickable
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={f.status === 'pendente' ? 'primary' : f.status === 'concluida' ? 'success' : f.status === 'rejeitada' ? 'error' : 'default'}
                          label={STATUS_FEATURE.find(s => s.value === f.status)?.label || f.status}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleOpenDialog('feature', f)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" color="error" onClick={() => handleDelete('feature', f.id, f.titulo)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pioneiros Tab */}
      {mainTab === 8 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* CRUD Table */}
          <Paper>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" fontWeight={700}>🏅 Pioneiros Pagali</Typography>
                <Typography variant="caption" color="text.secondary">
                  {pioneiroLoading ? 'A carregar…' : `${pioneiros.length} pioneiros registados`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={pioneiroLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                  disabled={pioneiroLoading}
                  onClick={reloadPioneiros}
                >
                  Atualizar
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setPioneiroDlgMode('add');
                    setPioneiroDlgData({ pontos: 650 });
                    setPioneiroDlg(true);
                  }}
                >
                  Adicionar Pioneiro
                </Button>
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell align="right">Pontos</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell width={100} align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pioneiroLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : pioneiros.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary" variant="body2">Sem pioneiros registados</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pioneiros.map((p: any) => (
                      <TableRow key={p.user_id || p.email} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>🏅</span>
                            <Typography variant="body2" fontWeight={600}>{p.nome || '—'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell><Typography variant="body2">{p.email}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{p.telefone || '—'}</Typography></TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={`${p.total_pontos} pts`} color="secondary" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {p.data_atribuicao ? new Date(p.data_atribuicao).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setPioneiroDlgMode('edit');
                                setPioneiroDlgData({ ...p, pontos: p.total_pontos });
                                setPioneiroDlg(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover estatuto Pioneiro">
                            <IconButton size="small" color="error" onClick={() => setDeleteConfirmDlg(p)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Notify Card */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              📧 Enviar notificação por email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Envia o email de Pioneiro a todos os {pioneiros.length} registados. Para um teste, preenche o campo "Email de teste".
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <TextField
                label="Email de teste (opcional)"
                size="small"
                placeholder="linda@exemplo.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                sx={{ minWidth: 260 }}
                helperText="Se preenchido, envia apenas para este email (dry-run)"
              />
              <Button
                variant="contained"
                color="primary"
                disabled={notifyLoading || pioneiros.length === 0}
                onClick={async () => {
                  setNotifyLoading(true);
                  setNotifyResult(null);
                  try {
                    if (testEmail.trim()) {
                      const r = await api.post('/admin/hub/pioneiros/notify', { canal: 'EMAIL', test_email: testEmail.trim() }, { timeout: 60000 });
                      setNotifyResult(r.data?.data || {});
                    } else {
                      const emails = pioneiros.map((p: any) => p.email).filter(Boolean);
                      const r = await api.post('/admin/hub/pioneiros/notify', { canal: 'EMAIL', emails }, { timeout: 300000 }); // 5 min para envio em massa
                      setNotifyResult(r.data?.data || {});
                    }
                  } catch (e: any) {
                    setNotifyResult({ error: e?.response?.data?.error || e.message });
                  } finally { setNotifyLoading(false); }
                }}
              >
                {notifyLoading
                  ? <CircularProgress size={18} color="inherit" />
                  : testEmail.trim()
                    ? '📧 Enviar teste'
                    : `📧 Notificar ${pioneiros.length} pioneiros`}
              </Button>
            </Box>
            {notifyResult && (
              <Alert severity={notifyResult.error ? 'error' : 'success'} sx={{ mt: 2 }}>
                {notifyResult.error ? notifyResult.error : (
                  <>
                    {notifyResult.dry_run && <strong>[DRY RUN] </strong>}
                    Enviados: <strong>{notifyResult.enviados}</strong> &nbsp;|&nbsp;
                    Falharam: <strong>{notifyResult.falharam}</strong> &nbsp;|&nbsp;
                    Total: <strong>{notifyResult.total_pioneiros}</strong>
                    {notifyResult.errors?.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption">Erros: {notifyResult.errors.join('; ')}</Typography>
                      </Box>
                    )}
                  </>
                )}
              </Alert>
            )}
          </Paper>

        </Box>
      )}

      {/* Pioneer Add/Edit Dialog */}
      <Dialog open={pioneiroDlg} onClose={() => !pioneiroDlgLoading && setPioneiroDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pioneiroDlgMode === 'add' ? '🏅 Adicionar Pioneiro' : '🏅 Editar Pioneiro'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {pioneiroDlgMode === 'add' ? (
              <TextField
                fullWidth
                label="Email do utilizador *"
                value={pioneiroDlgData.email || ''}
                onChange={(e) => setPioneiroDlgData({ ...pioneiroDlgData, email: e.target.value })}
                placeholder="utilizador@exemplo.com"
                autoFocus
              />
            ) : (
              <>
                <TextField fullWidth label="Nome" value={pioneiroDlgData.nome || ''} disabled />
                <TextField fullWidth label="Email" value={pioneiroDlgData.email || ''} disabled />
                <TextField fullWidth label="Telefone" value={pioneiroDlgData.telefone || ''} disabled />
              </>
            )}
            <TextField
              fullWidth
              label="Pontos *"
              type="number"
              value={pioneiroDlgData.pontos ?? 650}
              onChange={(e) => setPioneiroDlgData({ ...pioneiroDlgData, pontos: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0 }}
              helperText="Pontos atribuídos como Pioneiro (padrão: 650)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPioneiroDlg(false)} disabled={pioneiroDlgLoading}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={pioneiroDlgLoading || (pioneiroDlgMode === 'add' && !pioneiroDlgData.email?.trim())}
            onClick={async () => {
              setPioneiroDlgLoading(true);
              try {
                if (pioneiroDlgMode === 'add') {
                  await api.post('/admin/hub/pioneiros', {
                    email: pioneiroDlgData.email.trim(),
                    pontos: pioneiroDlgData.pontos ?? 650,
                  });
                } else {
                  await api.put(`/admin/hub/pioneiros/${pioneiroDlgData.user_id}`, {
                    pontos: pioneiroDlgData.pontos ?? 650,
                  });
                }
                setPioneiroDlg(false);
                await reloadPioneiros();
              } catch (e: any) {
                alert(e?.response?.data?.error || e.message || 'Erro ao guardar');
              } finally {
                setPioneiroDlgLoading(false);
              }
            }}
          >
            {pioneiroDlgLoading ? <CircularProgress size={18} color="inherit" /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pioneer Delete Confirm Dialog */}
      <Dialog open={!!deleteConfirmDlg} onClose={() => setDeleteConfirmDlg(null)} maxWidth="xs">
        <DialogTitle>Remover Pioneiro</DialogTitle>
        <DialogContent>
          <Typography>
            Tens a certeza que queres remover o estatuto de Pioneiro a{' '}
            <strong>{deleteConfirmDlg?.nome || deleteConfirmDlg?.email}</strong>?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Esta ação remove os pontos founding_member do utilizador.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDlg(null)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (!deleteConfirmDlg) return;
              try {
                await api.delete(`/admin/hub/pioneiros/${deleteConfirmDlg.user_id}`);
                setDeleteConfirmDlg(null);
                await reloadPioneiros();
              } catch (e: any) {
                alert(e?.response?.data?.error || e.message || 'Erro ao eliminar');
              }
            }}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedItem ? t('hub.editItem') : t('hub.newItem')}{' '}
          {dialogType === 'parceiro' ? t('hub.partnerLabel')
            : dialogType === 'app' ? t('hub.appLabel')
            : dialogType === 'nivel' ? t('hub.levelLabel')
            : dialogType === 'novidade' ? t('hub.newsLabel')
            : dialogType === 'recurso' ? 'Recurso'
            : 'Feature Request'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {/* Parceiro Form */}
            {dialogType === 'parceiro' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Nome"
                    value={formData.nome || ''}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Categoria"
                    value={formData.categoria || 'Outros'}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  >
                    {CATEGORIAS_PARCEIRO.map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Desconto"
                    value={formData.desconto || ''}
                    onChange={(e) => setFormData({ ...formData, desconto: e.target.value })}
                    placeholder="Ex: 10%"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Descrição"
                    value={formData.descricao || ''}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={formData.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.destaque || false}
                        onChange={(e) => setFormData({ ...formData, destaque: e.target.checked })}
                      />
                    }
                    label="Destaque"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={formData.status || 'ACTIVE'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="ACTIVE">{t('hub.statusActive')}</MenuItem>
                    <MenuItem value="INACTIVE">{t('hub.statusInactive')}</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {/* App Form */}
            {dialogType === 'app' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Nome"
                    value={formData.nome || ''}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Descrição"
                    value={formData.descricao || ''}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Categoria"
                    value={formData.categoria || 'Outros'}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  >
                    {CATEGORIAS_APP.map((cat) => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Ícone"
                    value={formData.icone || ''}
                    onChange={(e) => setFormData({ ...formData, icone: e.target.value })}
                    placeholder="shield, coffee, zap..."
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="URL"
                    value={formData.url || ''}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Preço (ECV)"
                    value={formData.preco || 0}
                    onChange={(e) => setFormData({ ...formData, preco: parseFloat(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Preço Original"
                    value={formData.preco_original || ''}
                    onChange={(e) => setFormData({ ...formData, preco_original: parseFloat(e.target.value) || null })}
                    placeholder="Opcional"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.gratuito || false}
                        onChange={(e) => setFormData({ ...formData, gratuito: e.target.checked, preco: e.target.checked ? 0 : formData.preco })}
                      />
                    }
                    label="Gratuito"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.destaque || false}
                        onChange={(e) => setFormData({ ...formData, destaque: e.target.checked })}
                      />
                    }
                    label="Destaque"
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={formData.status || 'ACTIVE'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="ACTIVE">{t('hub.statusActive')}</MenuItem>
                    <MenuItem value="INACTIVE">{t('hub.statusInactive')}</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {/* Nivel Form */}
            {dialogType === 'nivel' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Nome do Nível"
                    value={formData.nome || ''}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    placeholder="Ex: Bronze, Silver, Gold"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Pontos Mínimos"
                    value={formData.pontos_minimos || 0}
                    onChange={(e) => setFormData({ ...formData, pontos_minimos: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Pontos Máximos"
                    value={formData.pontos_maximos || 0}
                    onChange={(e) => setFormData({ ...formData, pontos_maximos: parseInt(e.target.value) || 0 })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Benefícios (JSON ou texto)"
                    value={formData.beneficios || ''}
                    onChange={(e) => setFormData({ ...formData, beneficios: e.target.value })}
                    placeholder='["Descontos básicos", "Suporte standard"]'
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Cor (Gradient)"
                    value={formData.cor || ''}
                    onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                    placeholder="from-amber-600 to-amber-700"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={formData.status || 'ACTIVE'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="ACTIVE">{t('hub.statusActive')}</MenuItem>
                    <MenuItem value="INACTIVE">{t('hub.statusInactive')}</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {/* Novidade Form */}
            {dialogType === 'novidade' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Título"
                    value={formData.titulo || ''}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Descrição (resumo)"
                    value={formData.descricao || ''}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Conteúdo"
                    value={formData.conteudo || ''}
                    onChange={(e) => setFormData({ ...formData, conteudo: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Tipo"
                    value={formData.tipo || 'news'}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  >
                    {TIPOS_NOVIDADE.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={formData.status || 'ACTIVE'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="ACTIVE">{t('hub.statusActive')}</MenuItem>
                    <MenuItem value="INACTIVE">{t('hub.statusInactive')}</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.destaque || false}
                        onChange={(e) => setFormData({ ...formData, destaque: e.target.checked })}
                      />
                    }
                    label="Destaque"
                  />
                </Grid>
              </>
            )}

            {/* Recurso Form */}
            {dialogType === 'recurso' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Título"
                    value={formData.titulo || ''}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Descrição curta"
                    value={formData.descricao || ''}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Conteúdo (resposta completa da FAQ, texto do guia, etc.)"
                    value={formData.conteudo || ''}
                    onChange={(e) => setFormData({ ...formData, conteudo: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Tipo"
                    value={formData.tipo || 'faq'}
                    onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  >
                    {TIPOS_RECURSO.map((tp) => (
                      <MenuItem key={tp.value} value={tp.value}>{tp.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Categoria"
                    value={formData.categoria || ''}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    placeholder="ex: pagamentos, wallet, suporte"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Ícone (lucide name)"
                    value={formData.icone || ''}
                    onChange={(e) => setFormData({ ...formData, icone: e.target.value })}
                    placeholder="message-circle, play-circle, book-open…"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Ordem"
                    type="number"
                    value={formData.ordem ?? 0}
                    onChange={(e) => setFormData({ ...formData, ordem: Number(e.target.value) })}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="URL (opcional)"
                    value={formData.url || ''}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    placeholder="/ajuda/tutorial ou mailto:suporte@pagali.cv"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={formData.status || 'ACTIVE'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="ACTIVE">{t('hub.statusActive')}</MenuItem>
                    <MenuItem value="INACTIVE">{t('hub.statusInactive')}</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}

            {/* Feature Request Form */}
            {dialogType === 'feature' && (
              <>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Título"
                    value={formData.titulo || ''}
                    onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Descrição"
                    value={formData.descricao || ''}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Categoria"
                    value={formData.categoria || ''}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    placeholder="pagamentos, wallet, social…"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    value={formData.status || 'VOTING'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    {STATUS_FEATURE.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.nome && !formData.titulo}
          >
            {selectedItem ? t('common.save') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Voters Dialog */}
      <Dialog open={votersDialog.open} onClose={() => setVotersDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ThumbUp color="primary" fontSize="small" />
          Votantes — {votersDialog.feature?.titulo}
          <Chip size="small" label={votersDialog.voters.length} sx={{ ml: 'auto' }} />
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {votersDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : votersDialog.voters.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Sem votos registados</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefone</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {votersDialog.voters.map((v: any, i: number) => (
                  <TableRow key={i} hover>
                    <TableCell>{v.nome || '—'}</TableCell>
                    <TableCell>{v.email || '—'}</TableCell>
                    <TableCell>{v.telefone || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {v.created_at ? new Date(v.created_at).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVotersDialog(d => ({ ...d, open: false }))}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════
// Pontos Config Section
// ═══════════════════════════════════════════════════════════
interface PontosConfig {
  tipo: string;
  divisor: number;
  descricao: string;
  ativo: boolean;
}

const TIPO_LABELS: Record<string, string> = {
  topup: 'Carregamento Wallet',
  pagamento: 'Pagamento com Wallet',
  compra_app: 'Compra de Mini-App',
  sisp_payment: 'Pagamento com Cartão',
  ticket_purchase: 'Compra de Bilhetes',
  recarga: 'Recarga Telemóvel',
};

const PontosConfigSection: React.FC = () => {
  const [configs, setConfigs] = useState<PontosConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [edited, setEdited] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
const { data } = await api.get('/admin/hub/pontos-config');
      if (data.success) setConfigs(data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (tipo: string) => {
    const divisor = edited[tipo];
    if (!divisor || divisor < 1) return;
    setSaving(tipo);
    try {
await api.put(`/admin/hub/pontos-config/${tipo}`, { divisor });
      await load();
      setEdited((p) => { const c = { ...p }; delete c[tipo]; return c; });
    } catch (e) { console.error(e); }
    finally { setSaving(null); }
  };

  const handleToggle = async (tipo: string, ativo: boolean) => {
    const current = configs.find((c) => c.tipo === tipo);
    if (!current) return;
    try {
await api.put(`/admin/hub/pontos-config/${tipo}`, { divisor: current.divisor, ativo });
      await load();
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Configuração de Pontos de Fidelidade</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define quantos CVE são necessários para ganhar 1 ponto em cada tipo de transação. Quanto menor o divisor, mais pontos o utilizador ganha.
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tipo de transação</TableCell>
              <TableCell align="center">Divisor (CVE por ponto)</TableCell>
              <TableCell align="center">Exemplo</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configs.map((c) => {
              const editedValue = edited[c.tipo];
              const currentValue = editedValue !== undefined ? editedValue : c.divisor;
              const isModified = editedValue !== undefined && editedValue !== c.divisor;
              return (
                <TableRow key={c.tipo}>
                  <TableCell>
                    <Typography fontWeight={600}>{TIPO_LABELS[c.tipo] || c.tipo}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.descricao}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      size="small"
                      value={currentValue}
                      onChange={(e) => setEdited((p) => ({ ...p, [c.tipo]: Number(e.target.value) }))}
                      inputProps={{ min: 1, style: { textAlign: 'center', width: '80px' } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.secondary">
                      1000 CVE = <strong>{Math.floor(1000 / currentValue)} pts</strong>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={c.ativo}
                      onChange={(e) => handleToggle(c.tipo, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {isModified && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSave(c.tipo)}
                        disabled={saving === c.tipo}
                      >
                        {saving === c.tipo ? 'A guardar...' : 'Guardar'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Como funciona:</strong> Se o divisor for 10, cada 10 CVE gastos = 1 ponto.
          Divisor 20 = 1 ponto por cada 20 CVE. Divisor 100 = 1 ponto por cada 100 CVE.
        </Typography>
      </Alert>
    </Paper>
  );
};

export default Hub;
